import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
} from 'firebase/firestore'
import { db } from '../firebase'
import { useFirestoreCollection } from './useFirestore'
import {
  CompanySettings,
  Employee,
  Lead,
  Material,
  ProCustomer,
  ProInvoice,
  ProJob,
  Product,
  Quote,
  TimeEntry,
} from '../types/pro'
import {
  seedCompany,
  seedCustomers,
  seedEmployees,
  seedInvoices,
  seedJobs,
  seedLeads,
  seedMaterials,
  seedQuotes,
  seedTimeEntries,
} from '../data/proSeed'
import { seedProducts } from '../data/products'
import { hydrateInvoice } from '../utils/invoice'
import {
  LegacyCustomer,
  LegacyTask,
  customerToPro,
  splitAddress,
  taskToJob,
  workLogToTimeEntries,
} from '../utils/proMigration'
import { JOB_NO_PATTERN, nextJobNo } from '../utils/pro'

const SETTINGS_DOC = 'proCompany'
const MIGRATION_DOC = 'proMigration'

/** Demo colleagues from the first build, removed now the company is one man. */
const RETIRED_DEMO_EMPLOYEES = ['mikkel@lykke-el.dk', 'anders@lykke-el.dk']

/**
 * Loads every Pro collection and, on first run, fills it.
 *
 * The import from the old system takes priority over the demo data: if the
 * old `customers` and `tasks` collections hold anything, those records are
 * carried across and any demo rows already sitting in the workspace are
 * cleared out, so real customers never share the list with invented ones.
 * A marker document makes sure this only ever happens once.
 */
export function useProWorkspace() {
  const customers = useFirestoreCollection<ProCustomer>('proCustomers')
  const employees = useFirestoreCollection<Employee>('proEmployees')
  const leads = useFirestoreCollection<Lead>('proLeads')
  const quotes = useFirestoreCollection<Quote>('proQuotes')
  const jobs = useFirestoreCollection<ProJob>('proJobs')
  const materials = useFirestoreCollection<Material>('proMaterials')
  const invoices = useFirestoreCollection<ProInvoice>('proInvoices')
  const timeEntries = useFirestoreCollection<TimeEntry>('proTimeEntries')
  const products = useFirestoreCollection<Product>('proProducts')

  const [settings, setSettings] = useState<CompanySettings>(seedCompany)
  const hasRun = useRef(false)
  const [preparing, setPreparing] = useState(false)

  useEffect(() => {
    const ref = doc(db, 'settings', SETTINGS_DOC)
    return onSnapshot(
      ref,
      (snapshot) => {
        if (snapshot.exists()) {
          setSettings({ ...seedCompany, ...(snapshot.data() as CompanySettings) })
        }
      },
      (error) => console.error('Error loading company settings:', error),
    )
  }, [])

  const saveSettings = useCallback(async (next: CompanySettings) => {
    setSettings(next)
    try {
      await setDoc(doc(db, 'settings', SETTINGS_DOC), next)
    } catch (error) {
      console.error('Error saving company settings:', error)
    }
  }, [])

  const loading =
    customers.loading ||
    employees.loading ||
    leads.loading ||
    quotes.loading ||
    jobs.loading ||
    materials.loading ||
    invoices.loading ||
    timeEntries.loading ||
    products.loading

  useEffect(() => {
    if (loading || hasRun.current) return
    hasRun.current = true

    const readLegacy = async () => {
      const [customerSnap, taskSnap] = await Promise.all([
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'tasks')),
      ])
      const legacyCustomers: LegacyCustomer[] = []
      customerSnap.forEach((entry) =>
        legacyCustomers.push({
          id: entry.id,
          ...entry.data(),
        } as LegacyCustomer),
      )
      const legacyTasks: LegacyTask[] = []
      taskSnap.forEach((entry) =>
        legacyTasks.push({ id: entry.id, ...entry.data() } as LegacyTask),
      )
      return { legacyCustomers, legacyTasks }
    }

    /** Clears whatever demo rows were written before the import ran. */
    const clearDemoData = async () => {
      await Promise.all([
        ...customers.data.map((row) => row.id && customers.remove(row.id)),
        ...jobs.data.map((row) => row.id && jobs.remove(row.id)),
        ...leads.data.map((row) => row.id && leads.remove(row.id)),
        ...quotes.data.map((row) => row.id && quotes.remove(row.id)),
        ...invoices.data.map((row) => row.id && invoices.remove(row.id)),
        ...timeEntries.data.map((row) => row.id && timeEntries.remove(row.id)),
      ])
    }

    const importLegacy = async (
      legacyCustomers: LegacyCustomer[],
      legacyTasks: LegacyTask[],
      employeeIds: string[],
    ) => {
      const idMap = new Map<string, string>()
      const addressMap = new Map<string, { address: string; city: string }>()

      for (const customer of legacyCustomers) {
        const newId = await customers.add(customerToPro(customer))
        if (customer.id) {
          idMap.set(customer.id, newId)
          const parts = splitAddress(customer.address)
          addressMap.set(customer.id, {
            address: parts.address,
            city: parts.city,
          })
        }
      }

      for (const task of legacyTasks) {
        const address = addressMap.get(task.customerId) ?? {
          address: '',
          city: '',
        }
        const jobId = await jobs.add(
          taskToJob(task, idMap.get(task.customerId) ?? '', address),
        )
        const entries = workLogToTimeEntries(task, jobId, employeeIds[0] ?? '')
        await Promise.all(entries.map((entry) => timeEntries.add(entry)))
      }
    }

    const seedDemo = async (employeeIds: string[]) => {
      const customerIds = await Promise.all(
        seedCustomers.map((customer) => customers.add(customer)),
      )

      await Promise.all(
        seedLeads.map((lead) =>
          leads.add({
            ...lead,
            ownerId: employeeIds[0] ?? '',
            customerId: lead.stage === 'vundet' ? customerIds[5] : undefined,
          }),
        ),
      )

      const jobIds = await Promise.all(
        seedJobs.map(({ customerIndex, ...job }) =>
          jobs.add({
            ...job,
            customerId: customerIds[customerIndex] ?? '',
            customerName: seedCustomers[customerIndex].name,
            employeeIds: [employeeIds[customerIndex % employeeIds.length] ?? ''],
          }),
        ),
      )

      await Promise.all(
        seedQuotes.map(({ customerIndex, ...quote }) =>
          quotes.add({
            ...quote,
            customerId: customerIds[customerIndex] ?? '',
            customerName: seedCustomers[customerIndex].name,
          }),
        ),
      )

      await Promise.all(
        seedInvoices.map(({ customerIndex, ...invoice }) =>
          invoices.add({
            ...invoice,
            customerId: customerIds[customerIndex] ?? '',
            customerName: seedCustomers[customerIndex].name,
          }),
        ),
      )

      await Promise.all(
        seedTimeEntries.map(({ jobIndex, employeeIndex, ...entry }) =>
          timeEntries.add({
            ...entry,
            jobId: jobIds[jobIndex] ?? '',
            employeeId: employeeIds[employeeIndex] ?? '',
          }),
        ),
      )
    }

    const run = async () => {
      setPreparing(true)
      try {
        // Crew and material catalogue have no equivalent in the old system.
        const employeeIds =
          employees.data.length > 0
            ? employees.data.map((employee) => employee.id ?? '')
            : await Promise.all(
                seedEmployees.map((employee) => employees.add(employee)),
              )

        if (materials.data.length === 0) {
          await Promise.all(seedMaterials.map((item) => materials.add(item)))
        }

        // Products are the company's own price list, not demo data — they
        // are seeded even when real customers were imported.
        if (products.data.length === 0) {
          await Promise.all(seedProducts.map((item) => products.add(item)))
        }

        // The demo crew was invented; clear them out for the one-man setup.
        await Promise.all(
          employees.data
            .filter((employee) => RETIRED_DEMO_EMPLOYEES.includes(employee.email))
            .map((employee) => employee.id && employees.remove(employee.id)),
        )

        const marker = await getDoc(doc(db, 'settings', MIGRATION_DOC))

        if (!marker.exists()) {
          const { legacyCustomers, legacyTasks } = await readLegacy()

          if (legacyCustomers.length > 0 || legacyTasks.length > 0) {
            await clearDemoData()
            await importLegacy(legacyCustomers, legacyTasks, employeeIds)
            await setDoc(doc(db, 'settings', MIGRATION_DOC), {
              migratedAt: new Date().toISOString(),
              customers: legacyCustomers.length,
              jobs: legacyTasks.length,
            })
            return
          }

          // Nothing to bring across — record that, so we never look again.
          await setDoc(doc(db, 'settings', MIGRATION_DOC), {
            migratedAt: new Date().toISOString(),
            customers: 0,
            jobs: 0,
          })
        }

        const isEmpty =
          customers.data.length === 0 &&
          leads.data.length === 0 &&
          jobs.data.length === 0
        if (isEmpty) await seedDemo(employeeIds)
      } catch (error) {
        console.error('Error preparing the Pro workspace:', error)
      } finally {
        setPreparing(false)
      }
    }

    run()
  }, [
    loading,
    customers,
    employees,
    leads,
    quotes,
    jobs,
    materials,
    invoices,
    timeEntries,
  ])

  /**
   * Every job carries an O-number. Records that arrived without one — from
   * the import, the calendar, or a quote — are numbered here, continuing
   * from the highest number already in use so nothing is reused.
   */
  useEffect(() => {
    if (loading || jobs.data.length === 0) return
    const missing = jobs.data.filter(
      (job) => !job.jobNo || !JOB_NO_PATTERN.test(job.jobNo),
    )
    if (missing.length === 0) return

    const taken = jobs.data.map((job) => job.jobNo)
    // Oldest first, so numbering follows the order the work came in.
    ;[...missing].reverse().forEach((job) => {
      if (!job.id) return
      const jobNo = nextJobNo(taken)
      taken.push(jobNo)
      jobs.update(job.id, { jobNo })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, jobs.data])

  /**
   * While the company is a one-man operation, every job and every hour
   * belongs to that one person. This keeps records that were imported
   * without an owner — or left over from the demo crew — pointing at them.
   */
  useEffect(() => {
    if (loading || employees.data.length !== 1) return
    const only = employees.data[0]?.id
    if (!only) return

    jobs.data
      .filter(
        (job) => job.employeeIds.length !== 1 || job.employeeIds[0] !== only,
      )
      .forEach((job) => {
        if (job.id) jobs.update(job.id, { employeeIds: [only] })
      })

    timeEntries.data
      .filter((entry) => entry.employeeId !== only)
      .forEach((entry) => {
        if (entry.id) timeEntries.update(entry.id, { employeeId: only })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, employees.data, jobs.data, timeEntries.data])

  /**
   * Manual re-run of the import, for when the automatic one was missed.
   * Replaces everything currently in the workspace with the old system's
   * customers and tasks.
   */
  const reimportLegacy = useCallback(async (): Promise<number> => {
    setPreparing(true)
    try {
      const [customerSnap, taskSnap] = await Promise.all([
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'tasks')),
      ])
      const legacyCustomers: LegacyCustomer[] = []
      customerSnap.forEach((entry) =>
        legacyCustomers.push({ id: entry.id, ...entry.data() } as LegacyCustomer),
      )
      const legacyTasks: LegacyTask[] = []
      taskSnap.forEach((entry) =>
        legacyTasks.push({ id: entry.id, ...entry.data() } as LegacyTask),
      )
      if (legacyCustomers.length === 0 && legacyTasks.length === 0) return 0

      await Promise.all([
        ...customers.data.map((row) => row.id && customers.remove(row.id)),
        ...jobs.data.map((row) => row.id && jobs.remove(row.id)),
        ...leads.data.map((row) => row.id && leads.remove(row.id)),
        ...quotes.data.map((row) => row.id && quotes.remove(row.id)),
        ...invoices.data.map((row) => row.id && invoices.remove(row.id)),
        ...timeEntries.data.map((row) => row.id && timeEntries.remove(row.id)),
      ])

      const employeeId = employees.data[0]?.id ?? ''
      const idMap = new Map<string, string>()
      const addressMap = new Map<string, { address: string; city: string }>()

      for (const customer of legacyCustomers) {
        const newId = await customers.add(customerToPro(customer))
        if (customer.id) {
          idMap.set(customer.id, newId)
          const parts = splitAddress(customer.address)
          addressMap.set(customer.id, {
            address: parts.address,
            city: parts.city,
          })
        }
      }

      for (const task of legacyTasks) {
        const address = addressMap.get(task.customerId) ?? {
          address: '',
          city: '',
        }
        const jobId = await jobs.add(
          taskToJob(task, idMap.get(task.customerId) ?? '', address),
        )
        const entries = workLogToTimeEntries(task, jobId, employeeId)
        await Promise.all(entries.map((entry) => timeEntries.add(entry)))
      }

      await setDoc(doc(db, 'settings', MIGRATION_DOC), {
        migratedAt: new Date().toISOString(),
        customers: legacyCustomers.length,
        jobs: legacyTasks.length,
      })

      return legacyCustomers.length + legacyTasks.length
    } finally {
      setPreparing(false)
    }
  }, [customers, employees, jobs, leads, quotes, invoices, timeEntries])

  // Older records predate customer type and the invoice billing block, so
  // they are completed on read instead of rewritten in the database.
  const hydratedCustomers = useMemo(
    () => ({
      ...customers,
      data: customers.data.map((customer) => ({
        ...customer,
        type:
          customer.type ??
          (customer.cvr || customer.contactPerson ? 'firma' : 'privat'),
      })),
    }),
    [customers],
  )

  const hydratedInvoices = useMemo(
    () => ({
      ...invoices,
      data: invoices.data.map((invoice) => hydrateInvoice(invoice)),
    }),
    [invoices],
  )

  return {
    customers: hydratedCustomers,
    employees,
    leads,
    quotes,
    jobs,
    materials,
    invoices: hydratedInvoices,
    timeEntries,
    products,
    settings,
    saveSettings,
    reimportLegacy,
    loading: loading || preparing,
  }
}
